/**
 * ExportConfirmSheet — modal/portal sheet shown when the user clicks
 * "Export to Download" on a VideoEditor node (or in the Timeline modal).
 *
 * Lets the user edit the new video node's name and validates that the name
 * doesn't collide with any existing node label on the canvas.  On confirm,
 * the parent dispatches `executeNode` with the chosen name carried via the
 * `in_export_name` schema field on the editor.
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { createNadDebug } from '@ponpon/api-client'

const exportSheetLog = createNadDebug('dw:video-editor:export-flow')

/**
 * Build the default export node name based on the current local time.
 * Format: `video_export_{MM}_{DD}_{HH}_{mm}`.
 */
export function buildDefaultExportName(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `video_export_${pad(now.getMonth() + 1)}_${pad(now.getDate())}_${pad(
    now.getHours(),
  )}_${pad(now.getMinutes())}`
}

export interface ExportConfirmSheetProps {
  isOpen: boolean
  defaultName: string
  /** Existing node labels on the canvas (used for duplicate validation). */
  existingLabels: ReadonlySet<string>
  onCancel: () => void
  onConfirm: (chosenName: string) => void
}

export const ExportConfirmSheet = memo(function ExportConfirmSheet({
  isOpen,
  defaultName,
  existingLabels,
  onCancel,
  onConfirm,
}: ExportConfirmSheetProps) {
  const [name, setName] = useState(defaultName)
  const inputRef = useRef<HTMLInputElement | null>(null)

  // Re-seed the input each time the sheet opens so the timestamp in the
  // default name reflects the moment the sheet appears.
  useEffect(() => {
    if (isOpen) {
      setName(defaultName)
      exportSheetLog({ event: 'open', defaultName })
      // Defer focus until after createPortal renders.
      requestAnimationFrame(() => {
        inputRef.current?.focus()
        inputRef.current?.select()
      })
    }
  }, [isOpen, defaultName])

  const trimmed = name.trim()

  const validation = useMemo<
    | { valid: true }
    | { valid: false; reason: 'empty' | 'duplicate' }
  >(() => {
    if (!trimmed) return { valid: false, reason: 'empty' }
    if (existingLabels.has(trimmed)) return { valid: false, reason: 'duplicate' }
    return { valid: true }
  }, [trimmed, existingLabels])

  const handleConfirm = useCallback(() => {
    if (!validation.valid) {
      exportSheetLog({ event: 'submit-blocked', reason: validation.reason, name: trimmed })
      return
    }
    exportSheetLog({ event: 'submit', name: trimmed })
    onConfirm(trimmed)
  }, [validation, trimmed, onConfirm])

  const handleCancel = useCallback(() => {
    exportSheetLog({ event: 'cancel' })
    onCancel()
  }, [onCancel])

  if (!isOpen) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleCancel()
      }}
      data-testid="export-confirm-sheet"
    >
      <div
        className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault()
            handleCancel()
          } else if (e.key === 'Enter') {
            e.preventDefault()
            handleConfirm()
          }
        }}
      >
        <h2 className="text-lg font-semibold text-foreground mb-2">Export to Download</h2>
        <p className="text-sm text-muted-foreground mb-4">
          This will export the video to a new Video node. Exported video can be downloaded.
        </p>
        <label className="block text-xs font-medium text-foreground mb-1.5">Node name</label>
        <input
          ref={inputRef}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full h-9 px-3 rounded-md bg-input-bg border border-input-border focus:border-input-border-hover focus:outline-none text-sm text-foreground"
          data-testid="export-confirm-name-input"
        />
        <div className="min-h-[18px] mt-1.5">
          {!validation.valid && validation.reason === 'duplicate' && (
            <p className="text-xs text-destructive" data-testid="export-confirm-error">
              A node with this name already exists. Choose a different name.
            </p>
          )}
          {!validation.valid && validation.reason === 'empty' && (
            <p className="text-xs text-destructive" data-testid="export-confirm-error">
              Name cannot be empty.
            </p>
          )}
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleCancel}
            className="h-9 px-4 rounded-md border border-input-border bg-surface-1 hover:bg-surface-2 text-foreground text-sm font-medium transition-colors"
            data-testid="export-confirm-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!validation.valid}
            className="h-9 px-4 rounded-md bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:hover:bg-amber-500 text-black text-sm font-semibold transition-colors flex items-center gap-1.5"
            data-testid="export-confirm-submit"
          >
            Export
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
})

ExportConfirmSheet.displayName = 'ExportConfirmSheet'
